import { apiDelete, apiGet, apiPatch, apiPost } from './client.js';

export type FormFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'yes_no'
  | 'rating'
  | 'date'
  | 'signature'
  | 'heading'
  | 'divider';

export type FormField = {
  id: string;
  type: FormFieldType | string;
  label: string;
  required?: boolean;
  order: number;
  options?: string[];
  min?: number;
  max?: number;
  placeholder?: string;
  helpText?: string;
  showIf?: { fieldId: string; value: string };
};

export type FormTriggerType =
  | 'manual'
  | 'on_ticket_create'
  | 'on_status_change'
  | 'on_customer_create'
  | 'on_checkout';

export type CustomForm = {
  id: string;
  tenantId: string;
  storeId: string | null;
  name: string;
  description: string | null;
  triggerType: FormTriggerType;
  triggerConfig: Record<string, unknown>;
  fields: FormField[];
  displayOnPos: boolean;
  sendToDisplay: boolean;
  requiresSignature: boolean;
  active: boolean;
  sortOrder: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

export type FormSubmission = {
  id: string;
  formId: string;
  formName: string | null;
  ticketId: string | null;
  customerId: string | null;
  fieldValues: Record<string, unknown>;
  signatureDataUrl: string | null;
  status: string;
  submittedBy: string | null;
  submittedVia: string;
  submittedAt: string | null;
  createdAt: string;
};

export type CreateFormBody = {
  name: string;
  description?: string;
  triggerType?: FormTriggerType;
  triggerConfig?: Record<string, unknown>;
  fields?: FormField[];
  displayOnPos?: boolean;
  sendToDisplay?: boolean;
  requiresSignature?: boolean;
  storeId?: string | null;
  sortOrder?: number;
};

export type UpdateFormBody = Partial<CreateFormBody>;

export type CreateSubmissionBody = {
  ticketId?: string;
  customerId?: string;
  fieldValues: Record<string, unknown>;
  signatureDataUrl?: string;
  submittedVia?: 'staff' | 'customer_display' | 'portal';
};

export function listForms(params?: {
  triggerType?: string;
  active?: boolean;
  storeId?: string;
}): Promise<CustomForm[]> {
  const qs = new URLSearchParams();
  if (params?.triggerType) qs.set('triggerType', params.triggerType);
  if (params?.active !== undefined) qs.set('active', String(params.active));
  if (params?.storeId) qs.set('storeId', params.storeId);
  const q = qs.toString();
  return apiGet<CustomForm[]>(`/api/v1/forms${q ? `?${q}` : ''}`).then((r) => r.data);
}

export function getForm(id: string): Promise<CustomForm> {
  return apiGet<CustomForm>(`/api/v1/forms/${id}`).then((r) => r.data);
}

export function createForm(body: CreateFormBody): Promise<CustomForm> {
  return apiPost<CustomForm>('/api/v1/forms', body).then((r) => r.data);
}

export function updateForm(id: string, body: UpdateFormBody): Promise<CustomForm> {
  return apiPatch<CustomForm>(`/api/v1/forms/${id}`, body).then((r) => r.data);
}

export function deleteForm(id: string): Promise<void> {
  return apiDelete(`/api/v1/forms/${id}`).then(() => undefined);
}

export function listSubmissions(params?: {
  ticketId?: string;
  customerId?: string;
  formId?: string;
  page?: number;
  pageSize?: number;
}): Promise<{ data: FormSubmission[]; meta: { page: number; pageSize: number; total: number } }> {
  const qs = new URLSearchParams();
  if (params?.ticketId) qs.set('ticketId', params.ticketId);
  if (params?.customerId) qs.set('customerId', params.customerId);
  if (params?.formId) qs.set('formId', params.formId);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
  const q = qs.toString();
  return apiGet<FormSubmission[], { page: number; pageSize: number; total: number }>(
    `/api/v1/forms/submissions${q ? `?${q}` : ''}`,
  ).then((r) => ({ data: r.data, meta: r.meta ?? { page: 1, pageSize: 25, total: r.data.length } }));
}

export function createSubmission(formId: string, body: CreateSubmissionBody): Promise<FormSubmission> {
  return apiPost<FormSubmission>(`/api/v1/forms/${formId}/submissions`, body).then((r) => r.data);
}

export function sendFormToDisplay(
  formId: string,
  body?: { ticketId?: string; customerId?: string },
): Promise<{ displayToken: string }> {
  return apiPost<{ displayToken: string }, { ticketId?: string; customerId?: string }>(
    `/api/v1/forms/${formId}/send-to-display`,
    body ?? {},
  ).then((r) => r.data);
}

export function listTriggered(params: {
  triggerType: string;
  status?: string;
  deviceCategory?: string;
}): Promise<CustomForm[]> {
  const qs = new URLSearchParams({ triggerType: params.triggerType });
  if (params.status) qs.set('status', params.status);
  if (params.deviceCategory) qs.set('deviceCategory', params.deviceCategory);
  return apiGet<CustomForm[]>(`/api/v1/forms/triggered?${qs.toString()}`).then((r) => r.data);
}
